import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AppHeader from "@/components/AppHeader";
import Tip from "@/components/Tip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  QrCode, Copy, Download, ExternalLink, Share2,
  Globe, Instagram, Facebook, Twitter, MessageCircle, Printer, Linkedin
} from "lucide-react";

function copy(text, label = "Copied") {
  navigator.clipboard?.writeText(text);
  toast.success(label);
}

const ChannelCard = ({ icon: Icon, color, name, description, caption, openUrl, testId }) => (
  <div className="card-3d bg-white rounded-2xl p-5 border border-slate-200" data-testid={testId}>
    <div className="flex items-start gap-3 mb-3">
      <div className="icon-3d h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon className="h-6 w-6" style={{ color }} />
      </div>
      <div className="flex-1">
        <div className="font-heading font-bold text-slate-900">{name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{description}</div>
      </div>
    </div>
    <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-700 leading-relaxed border border-slate-100 max-h-24 overflow-y-auto whitespace-pre-wrap">{caption}</div>
    <div className="mt-3 flex gap-2">
      <Button size="sm" variant="outline" onClick={() => copy(caption, "Caption copied")} className="flex-1" data-testid={`${testId}-copy`}>
        <Copy className="h-3.5 w-3.5 mr-1" /> Copy text
      </Button>
      {openUrl && (
        <Button size="sm" asChild className="flex-1 text-white" style={{ background: color }} data-testid={`${testId}-open`}>
          <a href={openUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1" /> Open</a>
        </Button>
      )}
    </div>
  </div>
);

export default function DistributionKit() {
  const { user } = useAuth();
  const [clinic, setClinic] = useState(null);
  const [linkInfo, setLinkInfo] = useState(null);
  const [customCaption, setCustomCaption] = useState("");

  const cid = user?.clinic_id;
  useEffect(() => {
    if (!cid) return;
    api.get(`/public/clinics/${cid}/whatsapp-link`).then(r => setLinkInfo(r.data)).catch(() => {});
    api.get(`/clinics/${cid}/dashboard`).then(r => setClinic(r.data?.clinic)).catch(() => {});
  }, [cid]);

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const qrPng = `${backendBase}/api/public/clinics/${cid}/whatsapp-qr.png`;
  const waLink = linkInfo?.url || "";
  const clinicName = clinic?.name || "your clinic";
  const baseCopy = `🩺 ${clinicName} is now on WhatsApp! 24/7 AI triage, instant appointments, prescriptions on chat — all in English / हिंदी / తెలుగు. Tap to chat:`;

  useEffect(() => {
    if (waLink && !customCaption) setCustomCaption(`${baseCopy}\n${waLink}`);
  }, [waLink]); // eslint-disable-line

  const channels = waLink ? [
    {
      icon: Globe, color: "#4285F4", name: "Google Business Profile",
      description: "Add this WhatsApp link to your contact info.",
      caption: `Open Google Business Profile → "Edit profile" → "Contact" → add this WhatsApp:\n${waLink}\n\nAlso paste this line in "From the business" section:\n${baseCopy} ${waLink}`,
      openUrl: "https://business.google.com/dashboard",
      testId: "channel-google",
    },
    {
      icon: Instagram, color: "#E1306C", name: "Instagram",
      description: "Bio link + Action Button → \"Chat on WhatsApp\".",
      caption: `${baseCopy}\n${waLink}\n\n📌 Put this link in your bio. Use Settings → Business Tools → Action Buttons → "WhatsApp" to add a tap-to-chat button on your profile.`,
      openUrl: "https://www.instagram.com/accounts/edit/",
      testId: "channel-instagram",
    },
    {
      icon: Facebook, color: "#1877F2", name: "Facebook Page",
      description: "Set up the WhatsApp button on your page.",
      caption: `${baseCopy}\n${waLink}\n\nPin this post + go to Page Settings → Templates and Tabs → enable the WhatsApp button with this number.`,
      openUrl: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(waLink)}&quote=${encodeURIComponent(baseCopy)}`,
      testId: "channel-facebook",
    },
    {
      icon: Twitter, color: "#000000", name: "Twitter / X",
      description: "Pin a tweet with click-to-chat.",
      caption: `${baseCopy}\n${waLink}`,
      openUrl: `https://twitter.com/intent/tweet?text=${encodeURIComponent(baseCopy)}&url=${encodeURIComponent(waLink)}`,
      testId: "channel-twitter",
    },
    {
      icon: Linkedin, color: "#0A66C2", name: "LinkedIn",
      description: "Share with your professional network.",
      caption: `Excited to share: ${clinicName} is now on WhatsApp with Pulse-Agent AI. Patients can chat 24/7 in EN / हिंदी / తెలుగు, get instant triage and prescriptions on chat.\n${waLink}`,
      openUrl: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(waLink)}`,
      testId: "channel-linkedin",
    },
    {
      icon: MessageCircle, color: "#25D366", name: "WhatsApp Status",
      description: "Share with your existing WhatsApp contacts.",
      caption: `${baseCopy}\n${waLink}\n\nOpen WhatsApp → Status → Camera → Text → paste this. Tap "My status".`,
      openUrl: null,
      testId: "channel-whatsapp",
    },
  ] : [];

  const downloadQr = async () => {
    try {
      const r = await fetch(qrPng);
      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = `${(clinicName).replace(/\s+/g, "_")}_pulse-agent_qr.png`;
      a.click(); URL.revokeObjectURL(u);
      toast.success("QR downloaded");
    } catch { toast.error("Download failed"); }
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: clinicName, text: baseCopy, url: waLink }); }
      catch { /* user cancelled */ }
    } else {
      copy(`${baseCopy}\n${waLink}`, "Caption + link copied");
    }
  };

  return (
    <div className="min-h-screen app-bg pb-16">
      <AppHeader title="Distribution Kit" subtitle="Get your WhatsApp number on every channel — in 5 minutes" badge={user?.role || ""} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 mt-6 sm:mt-8 space-y-5 sm:space-y-6">

        {/* Hero — WhatsApp number + QR */}
        <section className="card-3d bg-gradient-to-br from-[#004a99] to-[#0066d6] text-white rounded-2xl p-6 md:p-8 shadow-3d">
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="tiny-label opacity-80">Your Pulse-Agent WhatsApp</div>
              <h1 className="text-3xl md:text-4xl font-heading font-extrabold mt-1 leading-tight">
                <span className="gradient-text-pulse">{linkInfo?.clinic_name || clinicName}</span> is on WhatsApp
              </h1>
              <p className="text-white/85 text-sm md:text-base mt-2 max-w-2xl">Every patient who scans your QR or taps your WhatsApp link lands in a chat with <b>Clinic Pulse-Agent</b> — instant triage in EN / हिंदी / తెలుగు, prescriptions on chat, payments tracked.</p>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <div className="bg-white/10 backdrop-blur rounded-xl px-4 py-3 border border-white/20" data-testid="hero-wa-link">
                  <div className="tiny-label opacity-80">Click-to-chat link</div>
                  <div className="font-mono text-sm break-all">{waLink || "loading…"}</div>
                </div>
                <Button onClick={() => copy(waLink, "Link copied")} className="bg-white text-[#004a99] hover:bg-slate-100 btn-sheen" data-testid="hero-copy-link">
                  <Copy className="h-4 w-4 mr-1.5" /> Copy link
                </Button>
                <Button onClick={share} variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10" data-testid="hero-share">
                  <Share2 className="h-4 w-4 mr-1.5" /> Share
                </Button>
              </div>
            </div>

            {waLink && (
              <div className="bg-white rounded-2xl p-4 flex flex-col items-center" data-testid="hero-qr">
                <img src={qrPng} alt="WhatsApp QR" className="h-44 w-44 rounded-lg" />
                <Button size="sm" onClick={downloadQr} className="mt-3 bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid="hero-download-qr">
                  <Download className="h-3.5 w-3.5 mr-1" /> Download poster
                </Button>
                <p className="text-xs text-slate-500 mt-2 text-center max-w-[180px]">Print &amp; stick at reception, on letterheads &amp; prescription pads.</p>
              </div>
            )}
          </div>
        </section>

        {/* Custom caption editor */}
        <section className="card-3d bg-white rounded-2xl p-6 border border-slate-200" data-testid="caption-editor">
          <h2 className="text-lg font-heading font-bold flex items-center">
            <QrCode className="h-5 w-5 mr-2 text-[#f37021]" />
            Customize your master caption
            <Tip text="This caption is auto-prefilled into every channel card below. Edit it once here — all cards update." />
          </h2>
          <Textarea
            rows={4}
            value={customCaption}
            onChange={(e) => setCustomCaption(e.target.value)}
            className="mt-3 font-mono text-sm"
            data-testid="master-caption"
          />
          <div className="mt-2 flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => copy(customCaption, "Caption copied")} data-testid="copy-master-caption"><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
            <Button size="sm" variant="outline" onClick={() => setCustomCaption(`${baseCopy}\n${waLink}`)} data-testid="reset-master-caption">Reset to default</Button>
          </div>
        </section>

        {/* Channel cards */}
        <section>
          <h2 className="text-xl font-heading font-bold mb-1 flex items-center">
            <Share2 className="h-5 w-5 mr-2 text-[#004a99]" />
            6 channels · one-tap publish
            <Tip text="For Google / Instagram / Facebook the platforms don't have a public API — so we open the right editor page directly. You paste the pre-written copy in." />
          </h2>
          <p className="text-sm text-slate-500 mb-4">Each card: tap <b>Copy text</b>, tap <b>Open</b>, paste, save. Done in 5 minutes total.</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {channels.map((c, i) => (
              <ChannelCard key={i} {...c} caption={customCaption || c.caption} />
            ))}

            {/* Print poster as a card too */}
            <div className="card-3d bg-white rounded-2xl p-5 border border-slate-200" data-testid="channel-print">
              <div className="flex items-start gap-3 mb-3">
                <div className="icon-3d h-12 w-12 rounded-xl flex items-center justify-center bg-amber-50">
                  <Printer className="h-6 w-6 text-amber-700" />
                </div>
                <div className="flex-1">
                  <div className="font-heading font-bold text-slate-900">Print Poster</div>
                  <div className="text-xs text-slate-500 mt-0.5">Reception + letterheads.</div>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-700 border border-slate-100">A4 PNG with your QR + clinic name + "Chat on WhatsApp". Print &amp; stick.</div>
              <Button size="sm" onClick={downloadQr} className="mt-3 w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="channel-print-download">
                <Download className="h-3.5 w-3.5 mr-1" /> Download QR PNG
              </Button>
            </div>
          </div>
        </section>

        {/* How patients interact */}
        <section className="card-3d bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 border border-slate-200" data-testid="patient-journey">
          <h2 className="text-xl font-heading font-bold mb-4 flex items-center">
            <MessageCircle className="h-5 w-5 mr-2 text-emerald-600" />
            How patients will interact with you
          </h2>
          <ol className="space-y-3 text-sm text-slate-700">
            <li className="flex gap-3"><span className="icon-3d h-8 w-8 rounded-full bg-[#004a99] text-white flex items-center justify-center font-bold flex-shrink-0">1</span><div><b>Patient scans your QR / taps the WhatsApp link</b> — their phone opens a chat with you.</div></li>
            <li className="flex gap-3"><span className="icon-3d h-8 w-8 rounded-full bg-[#0066d6] text-white flex items-center justify-center font-bold flex-shrink-0">2</span><div><b>Pulse-Agent AI replies in 3 seconds</b> — asks symptoms, history, urgency. Speaks EN / हिंदी / తెలుగు.</div></li>
            <li className="flex gap-3"><span className="icon-3d h-8 w-8 rounded-full bg-[#f37021] text-white flex items-center justify-center font-bold flex-shrink-0">3</span><div><b>If urgent, your phone rings</b> — Twilio voice call. If routine, an appointment is auto-booked.</div></li>
            <li className="flex gap-3"><span className="icon-3d h-8 w-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold flex-shrink-0">4</span><div><b>You see everything on your dashboard</b> — triage transcript, voice notes, suggested prescription. Approve &amp; ship on WhatsApp.</div></li>
            <li className="flex gap-3"><span className="icon-3d h-8 w-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold flex-shrink-0">5</span><div><b>Money lands automatically</b> — invoice fires, Pulse-Agent reminds late-payers daily at 10 AM IST.</div></li>
          </ol>
        </section>

      </main>
    </div>
  );
}
